import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Table, Button, Tag, Space, Modal, Form, Input, Select, Switch, message, Typography } from 'antd';
import { PlusOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { useState } from 'react';
import { adminsApi } from '../api/client';

const { Title } = Typography;

export function Admins() {
  const queryClient = useQueryClient();
  const [modalOpen, setModalOpen] = useState(false);
  const [editingAdmin, setEditingAdmin] = useState<any>(null);
  const [form] = Form.useForm();

  const { data: admins, isLoading } = useQuery({
    queryKey: ['admins'],
    queryFn: async () => {
      const res = await adminsApi.getAll();
      return res.data.data;
    },
  });

  const createMutation = useMutation({
    mutationFn: (values: any) => adminsApi.create(values.telegramId, values.username, values.role),
    onSuccess: () => {
      message.success("Admin qo'shildi!");
      queryClient.invalidateQueries({ queryKey: ['admins'] });
      setModalOpen(false);
      form.resetFields();
    },
    onError: () => {
      message.error('Xatolik yuz berdi');
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => adminsApi.update(id, data),
    onSuccess: () => {
      message.success('Admin yangilandi!');
      queryClient.invalidateQueries({ queryKey: ['admins'] });
      setModalOpen(false);
      setEditingAdmin(null);
      form.resetFields();
    },
    onError: () => {
      message.error('Xatolik yuz berdi');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => adminsApi.delete(id),
    onSuccess: () => {
      message.success("Admin o'chirildi!");
      queryClient.invalidateQueries({ queryKey: ['admins'] });
    },
    onError: () => {
      message.error('Xatolik yuz berdi');
    },
  });

  const handleSubmit = (values: any) => {
    if (editingAdmin) {
      updateMutation.mutate({ id: editingAdmin.id, data: values });
    } else {
      createMutation.mutate(values);
    }
  };

  const columns = [
    {
      title: 'Username',
      dataIndex: 'username',
      key: 'username',
      render: (username: string) => username ? `@${username}` : '-',
    },
    {
      title: 'Telegram ID',
      dataIndex: 'telegramId',
      key: 'telegramId',
      render: (id: string) => id?.toString(),
    },
    {
      title: 'Rol',
      dataIndex: 'role',
      key: 'role',
      render: (role: string) => (
        <Tag color={role === 'SUPER_ADMIN' ? 'red' : 'blue'}>
          {role === 'SUPER_ADMIN' ? 'Super Admin' : 'Moderator'}
        </Tag>
      ),
    },
    {
      title: 'Holat',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (isActive: boolean) => (
        <Tag color={isActive ? 'green' : 'default'}>
          {isActive ? 'Faol' : 'Nofaol'}
        </Tag>
      ),
    },
    {
      title: 'Amallar',
      key: 'actions',
      render: (_: any, record: any) => (
        <Space>
          <Button
            icon={<EditOutlined />}
            onClick={() => {
              setEditingAdmin(record);
              form.setFieldsValue({
                ...record,
                telegramId: record.telegramId?.toString(),
              });
              setModalOpen(true);
            }}
          />
          <Button
            danger
            icon={<DeleteOutlined />}
            onClick={() => {
              Modal.confirm({
                title: "Adminni o'chirishni tasdiqlaysizmi?",
                okText: "O'chirish",
                cancelText: 'Bekor qilish',
                onOk: () => deleteMutation.mutate(record.id),
              });
            }}
          />
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <Title level={3}>Adminlar</Title>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => {
            setEditingAdmin(null);
            form.resetFields();
            setModalOpen(true);
          }}
        >
          Yangi admin
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={admins || []}
        loading={isLoading}
        rowKey="id"
        pagination={{ pageSize: 10 }}
      />

      <Modal
        title={editingAdmin ? "Adminni tahrirlash" : "Yangi admin qo'shish"}
        open={modalOpen}
        onCancel={() => {
          setModalOpen(false);
          setEditingAdmin(null);
          form.resetFields();
        }}
        onOk={() => form.submit()}
        okText={editingAdmin ? 'Saqlash' : "Qo'shish"}
        cancelText="Bekor qilish"
        confirmLoading={createMutation.isPending || updateMutation.isPending}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item
            name="telegramId"
            label="Telegram ID"
            rules={[{ required: true, message: 'Telegram ID kiriting' }]}
          >
            <Input placeholder="123456789" disabled={!!editingAdmin} />
          </Form.Item>
          
          <Form.Item name="username" label="Username">
            <Input placeholder="username" />
          </Form.Item>
          
          <Form.Item
            name="role"
            label="Rol"
            rules={[{ required: true }]}
            initialValue="MODERATOR"
          >
            <Select>
              <Select.Option value="MODERATOR">Moderator</Select.Option>
              <Select.Option value="SUPER_ADMIN">Super Admin</Select.Option>
            </Select>
          </Form.Item>
          
          {editingAdmin && (
            <Form.Item name="isActive" label="Faol" valuePropName="checked">
              <Switch />
            </Form.Item>
          )}
        </Form>
      </Modal>
    </div>
  );
}
